/**
 * Recent charge ledger
 *
 * Core recalculates `Fundraiser.Finances.RaisedAmount` and the donation plan
 * charge fields asynchronously after a charge. For a window afterwards, every
 * staleness-sensitive guard on this screen (and the API's own
 * `Id.fundraiserRaisedAmount` check) reads out-of-date data, so a repeat charge
 * would go straight through and take a second payment. That window is exactly
 * when an operator is most likely to re-run a batch, having just watched some
 * rows fail.
 *
 * This keeps a short-lived record of what was attempted so those ids can be
 * blocked. It is per browser and per machine: it reduces the most likely repeat
 * charge scenario, it does not close the hole. The real fix is a duplicate
 * charge guard in core (GR-3781 section B4).
 *
 * The decision logic is pure and takes an injected `now`, so it is testable
 * without touching storage or the clock.
 */

import {
  RECENT_CHARGES_STORAGE_KEY,
  RECENT_CHARGE_LEDGER_VERSION,
  RECENT_CHARGE_RETENTION_MS,
  RECENT_CHARGE_WINDOW_MS,
  recentChargeOutcome
} from './constants';
import { OutcomeClass, RecentChargeEntry, RecentChargeLedger } from './types';

const EMPTY_LEDGER: RecentChargeLedger = {
  version: RECENT_CHARGE_LEDGER_VERSION,
  entries: []
};

/* ────────────────────────── pure logic ────────────────────────── */

const parseTime = (value: string): number => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Drop entries past the retention cutoff so the stored key stays small. */
export const pruneEntries = (
  entries: RecentChargeEntry[],
  now: number
): RecentChargeEntry[] =>
  entries.filter((entry) => now - parseTime(entry.attemptedAtUtc) < RECENT_CHARGE_RETENTION_MS);

/**
 * Whether an entry still blocks a re-charge.
 *
 * A `rejected` outcome never blocks: nothing moved, so there is nothing to go
 * stale. An entry still marked `attempted` does block, because the tab died
 * before the outcome was known and that is the least safe state of all.
 */
export const isWithinWindow = (entry: RecentChargeEntry, now: number): boolean => {
  if (entry.outcome === recentChargeOutcome.rejected) {
    return false;
  }
  const elapsed = now - parseTime(entry.attemptedAtUtc);
  return elapsed >= 0 && elapsed < RECENT_CHARGE_WINDOW_MS;
};

const MINUTE_MS = 60 * 1000;

/** Whole minutes, floored, with a 1 minute floor so it never reads "0 minutes". */
const toMinutes = (ms: number): number => Math.max(1, Math.floor(ms / MINUTE_MS));

export interface RecentChargeDescription {
  elapsedMinutes: number;
  remainingMinutes: number;
}

export const describeRecentCharge = (
  entry: RecentChargeEntry,
  now: number
): RecentChargeDescription => {
  const elapsed = Math.max(0, now - parseTime(entry.attemptedAtUtc));
  return {
    elapsedMinutes: toMinutes(elapsed),
    remainingMinutes: toMinutes(Math.max(0, RECENT_CHARGE_WINDOW_MS - elapsed))
  };
};

/** The blocking entry for an id, or null. */
export const findBlockingEntry = (
  entries: RecentChargeEntry[],
  id: number,
  now: number
): RecentChargeEntry | null =>
  entries.find((entry) => entry.id === id && isWithinWindow(entry, now)) ?? null;

/* ─────────────────────── storage access ─────────────────────── */

/*
 * Every access is defensive. A blocked, full or corrupted localStorage must
 * degrade to "no ledger" rather than break a screen that charges cards.
 */

const isLedger = (value: unknown): value is RecentChargeLedger =>
  !!value &&
  typeof value === 'object' &&
  Array.isArray((value as RecentChargeLedger).entries);

export const readLedger = (now: number): RecentChargeLedger => {
  try {
    const raw = localStorage.getItem(RECENT_CHARGES_STORAGE_KEY);
    if (!raw) {
      return EMPTY_LEDGER;
    }
    const parsed = JSON.parse(raw);
    if (!isLedger(parsed) || parsed.version !== RECENT_CHARGE_LEDGER_VERSION) {
      return EMPTY_LEDGER;
    }
    const entries = pruneEntries(
      parsed.entries.filter(
        (entry) => typeof entry?.id === 'number' && typeof entry?.attemptedAtUtc === 'string'
      ),
      now
    );
    return { version: RECENT_CHARGE_LEDGER_VERSION, entries };
  } catch {
    return EMPTY_LEDGER;
  }
};

const writeLedger = (ledger: RecentChargeLedger): void => {
  try {
    localStorage.setItem(RECENT_CHARGES_STORAGE_KEY, JSON.stringify(ledger));
  } catch {
    // Storage full or unavailable. The ledger is a safety net, not a
    // prerequisite, so carry on without it.
  }
};

/**
 * Record an attempt. Called immediately BEFORE the charge request, not after,
 * so a crashed tab or a closed browser mid-charge still leaves a record.
 */
export const recordAttempt = (id: number, now: number): void => {
  const ledger = readLedger(now);
  const entries = ledger.entries.filter((entry) => entry.id !== id);
  entries.push({
    id,
    attemptedAtUtc: new Date(now).toISOString(),
    outcome: recentChargeOutcome.attempted
  });
  writeLedger({ version: RECENT_CHARGE_LEDGER_VERSION, entries });
};

/** Fill in the outcome once the request settles, keeping the original time. */
export const patchOutcome = (id: number, outcome: OutcomeClass, now: number): void => {
  const ledger = readLedger(now);
  const entries = ledger.entries.map((entry) =>
    entry.id === id ? { ...entry, outcome } : entry
  );
  writeLedger({ version: RECENT_CHARGE_LEDGER_VERSION, entries });
};

export const clearLedger = (): void => {
  try {
    localStorage.removeItem(RECENT_CHARGES_STORAGE_KEY);
  } catch {
    // Nothing useful to do here.
  }
};
